import type { IJob } from '../../../../stores/JobStore/Job';
import useJobActionStatus from './useJobActionStatus';
import { JobAction } from '../actions';
import type React from 'react';
import {
  IconNewWindow,
  IconEdit,
  IconCopy,
  IconDelete,
  IconDownload,
  IconMail,
  IconShare,
  Text,
  IconPersonAdd,
} from 'braid-design-system';
import ShareIcons from '../../../common/ShareIcons';
import StatsIcon from '../icons/StatsIcon';
import RefreshIcon from '../icons/RefreshIcon';
import translations from '../.vocab';
import { useTranslations } from '@vocab/react';
import type { EmployerSiteNames } from '@seek/hirer-graphql-client/lib-types/constants/headers';

interface Option {
  value: JobAction;
  label: string;
  disabled: boolean;
  disableDescription?: React.ReactNode;
  content: React.ReactNode;
  icon?: React.ReactNode;
}

export const useJobActionOptions = ({
  job,
  showExportToCsv,
  showTalentSearchLink,
  site,
}: {
  job: IJob;
  showExportToCsv: boolean;
  showTalentSearchLink: boolean;
  site: EmployerSiteNames;
}): Option[] => {
  const { t } = useTranslations(translations);

  const jobActionStatus = useJobActionStatus(job, site);

  return [
    {
      value: JobAction.VIEW,
      label: t('View Job'),
      ...jobActionStatus.viewActionStatus,
      content: (
        <Text tone="secondary" size="small">
          {t('View your job ad')}
        </Text>
      ),
      icon: <IconNewWindow tone="secondary" />,
    },
    showTalentSearchLink
      ? [
          {
            value: JobAction.TALENT_SEARCH,
            label: t('Talent Search'),
            content: (
              <Text tone="secondary" size="small">
                {t('Browse candidates who match this job')}
              </Text>
            ),
            disabled: false,
            icon: <IconNewWindow tone="secondary" />,
          },
        ]
      : [],
    {
      value: JobAction.VIEW_AD_INFORMATION,
      label: t('View ad information'),
      disabled: false,
      content: (
        <Text tone="secondary" size="small">
          {t("Details about your job ad and how it's performing")}
        </Text>
      ),
      icon: <StatsIcon />,
    },
    {
      value: JobAction.EDIT,
      label: t('Edit'),
      ...jobActionStatus.editActionStatus,
      content: (
        <Text tone="secondary" size="small">
          {t('Edit job details')}
        </Text>
      ),
      icon: <IconEdit tone="secondary" />,
    },
    showExportToCsv
      ? [
          {
            value: JobAction.EXPORT_CANDIDATE_LIST,
            label: t('Export candidate list'),
            disabled: false,
            content: (
              <Text tone="secondary" size="small">
                {t(
                  'Download a .csv file with details of all candidates for this job',
                )}
              </Text>
            ),
            icon: <IconDownload tone="secondary" />,
          },
        ]
      : [],
    {
      value: JobAction.NOTIFICATION_PREFERENCE,
      label: t('Notifications'),
      ...jobActionStatus.notificationActionStatus,
      content: (
        <Text tone="secondary" size="small">
          {t('Set how often you receive email notifications for this job')}
        </Text>
      ),
      icon: <IconMail tone="secondary" />,
    },
    {
      value: JobAction.TEAM_MEMBER_ACCESS,
      label: t('Team Access'),
      ...jobActionStatus.teamAccessActionStatus,
      content: (
        <Text tone="secondary" size="small">
          {t('Give access to your team and invite them to this job')}
        </Text>
      ),
      icon: <IconPersonAdd tone="secondary" />,
    },
    {
      value: JobAction.REFRESH,
      label: t('Refresh'),
      ...jobActionStatus.refreshActionStatus,
      content: (
        <Text tone="secondary" size="small">
          {t('Refresh and extend your ad')}
        </Text>
      ),
      icon: <RefreshIcon />,
    },
    {
      value: JobAction.COPY,
      label: t('Copy'),
      ...jobActionStatus.copyActionStatus,
      content: (
        <Text tone="secondary" size="small">
          {t('Create a new job by copying the details of this job')}
        </Text>
      ),
      icon: <IconCopy tone="secondary" />,
    },
    {
      value: JobAction.EXPIRE,
      label: t('Expire'),
      ...jobActionStatus.expireActionStatus,
      content: (
        <Text tone="secondary" size="small">
          {t(
            'Remove your job from SEEK - you can continue to manage your candidates as normal',
          )}
        </Text>
      ),
      icon: <IconDelete tone="secondary" />,
    },
    {
      value: JobAction.SHARE,
      label: t('Share job'),
      ...jobActionStatus.shareActionStatus,
      content: <ShareIcons job={job} />,
      icon: <IconShare tone="secondary" />,
    },
  ].flatMap((options) => options);
};
